/**
 * Wo ein Satz wirklich endet. (06.09.2026, Ticket #2130)
 *
 * ## Woher das kommt
 *
 * Martin, zweimal an einem Tag:
 *
 *   *„Beim Vorlesen im Chat gibt es immer wieder Lesefehler und Aussetzer.
 *   Teste ich denselben Text direkt bei Soniox, funktioniert es."*
 *
 *   *„Es werden Saetze abgeschnitten teilweise. Kann man bitte die Saetze
 *   vollstaendig auditiv ausgeben lassen? Und irgendwie ist die Qualitaet
 *   von dem Soniox ploetzlich so schlecht, weil er nicht einmal mehr die
 *   Woerter richtig aussprechen kann."*
 *
 * BEIDES ist dasselbe: das Zerschneiden vor der Sprachausgabe. Gemessen am
 * 06.09. gegen die Soniox-Schnittstelle: derselbe Text am Stueck kommt
 * vollstaendig und richtig zurueck (273 Zeichen rein, 18,8 s Ton, per
 * Rueck-Transkription Wort fuer Wort geprueft). Der Anbieter schneidet also
 * NICHTS ab. Wir schneiden.
 *
 * Die alte Regel im Chat war `/([.!?]["')\]]?)\s+/` — sie haelt JEDEN Punkt
 * mit Leerzeichen dahinter fuer ein Satzende:
 *
 *     „Der Termin ist am 18. September 2026, also in ca. 12 Tagen."
 *
 * zerfiel zu „Der Termin ist am 18." | „September 2026, also in ca." |
 * „12 Tagen." Landet so ein Schnitt auf einer Stueck-Grenze, bekommt die
 * Sprachausgabe „…am 18." als vollstaendige Anfrage und liest sie auch so.
 * Genau die Lesefehler und die abgeschnittenen Saetze, die er hoert.
 *
 * ## Die Regel
 *
 * Ein Punkt beendet einen Satz nur, wenn er NICHT
 *   - auf eine ein- oder zweistellige Zahl folgt (18. September, 3.5),
 *   - auf eine bekannte Abkuerzung folgt (ca., z. B., Nr.),
 *   - auf einen einzelnen Grossbuchstaben folgt (Initialen),
 * und wenn danach Textende kommt oder ein Zwischenraum mit einem neuen,
 * gross beginnenden Wort.
 *
 * ⚠️ Im Zweifel NICHT trennen. Ein zu langer Satz wird notfalls hart
 *    geschnitten; ein zerrissenes Datum ist ein hoerbarer Fehler.
 *
 * ⚠️ Diese Datei ist die EINZIGE Stelle, an der diese Regel steht. Sie hat
 *    vorher an drei Stellen in drei Fassungen gestanden — nur eine davon war
 *    richtig, und es war nicht die, die im Chat lief.
 */

const ABKUERZUNGEN = new Set([
  "z", "b", "d", "h", "u", "a", "s", "o", "ca", "bzw", "evtl", "ggf", "inkl",
  "exkl", "max", "min", "Nr", "nr", "Abs", "Art", "Dr", "Prof", "Ing", "Str",
  "bspw", "usw", "uvm", "vgl", "sog", "ggü", "Mio", "Mrd", "Tel", "Fa", "St",
  "Hr", "Fr", "Jan", "Feb", "Mär", "Apr", "Jun", "Jul", "Aug", "Sep", "Sept",
  "Okt", "Nov", "Dez",
]);

/**
 * Die Stellen, an denen ein Satz endet — jeweils der Index HINTER dem
 * Satzzeichen.
 *
 * @param nurAbgeschlossen Fuer laufenden Text aus einem Datenstrom: dann
 *   zaehlt ein Satzzeichen am Pufferende NICHT als Ende, weil der Satz mit
 *   dem naechsten Stueck weitergehen kann. („Der Termin ist am 18." koennte
 *   „…18. September" werden.)
 */
export function satzEnden(text: string, nurAbgeschlossen = false): number[] {
  const enden: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const z = text[i];
    if (z !== "." && z !== "!" && z !== "?") continue;
    // Mehrere Satzzeichen hintereinander („…!?") gemeinsam nehmen.
    let j = i;
    while (j + 1 < text.length && ".!?".includes(text[j + 1] ?? "")) j++;
    const danach = text.slice(j + 1);
    const naechstes = /^\s*(\S)/.exec(danach);
    if (naechstes) {
      const c = naechstes[1] ?? "";
      // Ein Satz beginnt gross (oder mit einem Anfuehrungszeichen).
      const beginntGross = c === c.toUpperCase() && c !== c.toLowerCase();
      if (!beginntGross && !"„\"'(".includes(c)) continue;
      // Kein Zwischenraum dazwischen? Dann ist es keine Satzgrenze.
      if (!/^\s/.test(danach)) continue;
    } else if (nurAbgeschlossen && !/^\s/.test(danach)) {
      /*
       * Nichts mehr dahinter, nicht einmal ein Zwischenraum: der Satz kann
       * mit dem naechsten Stueck weitergehen, also noch nicht schneiden.
       *
       * ⚠️ Steht ein Zwischenraum da, wird SEHR WOHL geschnitten. Alles
       *    andere kostete Latenz: der letzte fertige Satz jedes Stroms
       *    muesste auf das naechste Stueck warten, und beim ersten Satz
       *    haengt daran, wann ueberhaupt Ton kommt. Die zweifelhaften Faelle
       *    („am 18. ") faengt die Ziffern- und Abkuerzungsregel unten ab —
       *    dafuer braucht sie das naechste Wort nicht.
       */
      continue;
    }
    if (z === ".") {
      const davor = text.slice(Math.max(0, i - 12), i);
      /*
       * Ziffer davor: Datum, Ordnungszahl, Kommastelle — nicht trennen.
       *
       * ⚠️ Ausnahme: eine Zahl mit drei oder mehr Stellen ist keine
       *    Ordnungszahl mehr („2026.", „3411."). Dort DARF getrennt werden,
       *    sonst wachsen die Stuecke unnoetig. Bei ein bis zwei Stellen
       *    bleibt es beim Nichttrennen: „18. September" ist haeufiger als
       *    ein Satz, der auf „18." endet.
       */
      const ziffern = /(\d+)$/.exec(davor)?.[1] ?? "";
      if (ziffern.length > 0 && ziffern.length <= 2) continue;
      /*
       * ⚠️ 08.09.2026 — `\p{L}` statt einer Buchstabenliste.
       *
       * Hier stand `[A-Za-zÄÖÜäöüß]`. Bei „… como están. Das heisst:" fing
       * die Regel damit nur das „n" hinter dem „á" — ein Buchstabe, also
       * „Initiale", also KEINE Satzgrenze. Zwei Saetze verschmolzen zu
       * einem.
       *
       * Gefunden beim Sprachwechsel fuers Vorlesen (Martins Testsaetze vom
       * 08.09.), aber der Schaden ist groesser als das: Jeder Satz, der auf
       * einen Akzent-Buchstaben endet, klebte am naechsten. Das trifft
       * spanische und franzoesische Woerter genauso wie Namen — und
       * ueberlange Stuecke sind bei Soniox der Weg in die 180-Sekunden-Decke.
       */
      const wort = /(\p{L}+)$/u.exec(davor)?.[1] ?? "";
      if (wort.length === 1) continue; // Initiale
      if (ABKUERZUNGEN.has(wort)) continue;
    }
    enden.push(j + 1);
    i = j;
  }
  return enden;
}

/** Was hinter dem Satzzeichen noch zum Satz gehört: schließende Anführungszeichen und Klammern. */
const SCHLIESSEND = /["'’“”»«›‹)\]]+$/u;

/**
 * Endet dieses Stück mit einem Satz? (27.09.2026, Ticket 2692)
 *
 * Die Vorlese-Schlange legt danach eine Satzpause ein. „!", „?" und „…"
 * enden immer, auch vor einem schließenden Anführungszeichen oder einer
 * Klammer. Ein Punkt nur, wenn `satzEnden` ihn als Ende nimmt — „am 18."
 * oder „ca." am Stückende sind keine Satzgrenze.
 */
export function endetMitSatz(text: string): boolean {
  const kern = text.trimEnd().replace(SCHLIESSEND, "");
  if (/[!?…]$/u.test(kern)) return true;
  return kern.endsWith(".") && satzEnden(kern).includes(kern.length);
}

/**
 * Wo man innerhalb eines Satzes Luft holt: hinter Komma, Doppelpunkt,
 * Semikolon oder Gedankenstrich, vor einem Zwischenraum. EINE Fassung für
 * `splitLongSentence` und `ersterSchnitt` (27.09.2026, Ticket 2692).
 */
export const KLAUSEL_GRENZE = /[,:;—–]\s/;

/** Einen Text in echte Saetze zerlegen. Regel und Beleg siehe oben. */
export function teileInSaetze(text: string): string[] {
  const saetze: string[] = [];
  let start = 0;
  for (const ende of satzEnden(text)) {
    saetze.push(text.slice(start, ende));
    start = ende;
  }
  if (start < text.length) saetze.push(text.slice(start));
  return saetze.filter((s) => s.length > 0);
}
